import { useEffect, useState, FormEvent } from 'react';
import { api, type Me, type TenantRow, type LicenseRow } from '../lib/api';

function fmtDate(ms: number | null | undefined) {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString();
}
function daysUntil(ms: number | null | undefined): number | null {
  if (!ms) return null;
  return Math.round((ms - Date.now()) / 86400000);
}
function expirationPill(end_date: number | null | undefined) {
  const d = daysUntil(end_date);
  if (d === null) return <span className="pill bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">no date</span>;
  if (d < 0)       return <span className="pill-err">expired {-d}d ago</span>;
  if (d <= 30)     return <span className="pill-err">{d}d left</span>;
  if (d <= 60)     return <span className="pill-warn">{d}d left</span>;
  return <span className="pill-ok">{d}d left</span>;
}

export default function TenantsAdmin({ me }: { me: Me }) {
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  async function load() {
    try { setErr(null); const r = await api.tenantsList(); setTenants(r.tenants); }
    catch (e: any) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  if (me.role !== 'super_admin' && me.role !== 'tenant_admin') {
    return <div className="card p-8 text-center text-gray-500">Tenant management requires admin role.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tenants</h1>
          <p className="text-gray-500 text-sm mt-1">
            {me.role === 'super_admin'
              ? 'Create, clone, and manage customer tenants. Each tenant gets a license with expiration tracking.'
              : 'Your tenant subscription, contact, and license window.'}
          </p>
        </div>
        {me.role === 'super_admin' && (
          <button onClick={() => setShowCreate(true)} className="btn-primary text-sm">+ New tenant</button>
        )}
      </div>

      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}

      {tenants === null && !err && <div className="card p-8 text-center text-gray-500">Loading…</div>}

      {tenants && (
        <div className="card divide-y divide-tnh-border dark:divide-tnh-border-dark">
          {tenants.map((t) => (
            <TenantRowComp key={t.tenant_id} t={t} me={me} onChange={load} />
          ))}
          {tenants.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-gray-500">No tenants yet.</div>
          )}
        </div>
      )}

      {showCreate && (
        <CreateTenantModal
          existing={tenants ?? []}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function TenantRowComp({ t, me, onChange }: { t: TenantRow; me: Me; onChange: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [activeLic, setActiveLic] = useState<LicenseRow | null | undefined>(undefined);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    if (!expanded || activeLic !== undefined) return;
    api.tenantsGet(t.tenant_id).then((r) => setActiveLic(r.active_license)).catch(() => setActiveLic(null));
  }, [expanded, t.tenant_id, activeLic]);

  const modules: string[] = (() => { try { return JSON.parse(t.modules_enabled || '[]'); } catch { return []; } })();
  const canEdit    = me.role === 'super_admin' || (me.role === 'tenant_admin' && me.tenant_id === t.tenant_id);
  const canArchive = me.role === 'super_admin' && t.tenant_id !== 'longbeach' && t.status !== 'deleted';
  const canSuspend = me.role === 'super_admin' && t.tenant_id !== 'longbeach' && t.status === 'active';
  const canResume  = me.role === 'super_admin' && t.status === 'suspended';

  async function setStatus(next: 'active' | 'suspended') {
    const verb = next === 'suspended' ? 'Suspend' : 'Resume';
    if (!confirm(`${verb} tenant "${t.tenant_id}"?${next === 'suspended' ? ' Their console + API access will be blocked.' : ''}`)) return;
    try { await api.tenantsUpdate(t.tenant_id, { status: next }); onChange(); }
    catch (e: any) { alert(e.message); }
  }

  return (
    <div className="px-4 py-3">
      <div onClick={() => setExpanded((v) => !v)} className="cursor-pointer">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono font-medium text-sm">{t.tenant_id}</span>
          <span className="text-sm font-medium">{t.name}</span>
          {t.status === 'active' ? <span className="pill-ok">active</span>
            : t.status === 'suspended' ? <span className="pill-warn">suspended</span>
            : t.status === 'deleted' ? <span className="pill-err">archived</span>
            : <span className="pill-warn">{t.status}</span>}
          {expirationPill(t.end_date)}
          <span className="pill bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">{t.subscription_tier}</span>
          {t.cloned_from && <span className="pill bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">cloned from {t.cloned_from}</span>}
        </div>
        <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-3">
          <span>billing: {t.billing_email || '—'}</span>
          <span>·</span>
          <span>start: {fmtDate(t.start_date)}</span>
          <span>·</span>
          <span>end: {fmtDate(t.end_date)}</span>
          <span>·</span>
          <span>{modules.length} modules</span>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-tnh-border dark:border-tnh-border-dark space-y-3 text-sm">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Enabled modules</div>
            <div className="flex flex-wrap gap-1.5">
              {modules.length === 0 ? <span className="text-xs text-gray-400">none</span> :
                modules.map((m) => <span key={m} className="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{m}</span>)}
            </div>
          </div>
          {activeLic && (
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Active license</div>
              <div className="text-xs">
                <span className="font-mono">{activeLic.license_id}</span> · {activeLic.plan_name} · {activeLic.term_years}y term · ${(activeLic.plan_amount / 100).toFixed(2)} {activeLic.currency}/yr
              </div>
              {activeLic.xero_invoice_id && <div className="text-xs text-gray-500">Xero invoice: <span className="font-mono">{activeLic.xero_invoice_id}</span></div>}
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            {canEdit && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowEdit(true); }}
                className="btn-primary text-sm"
              >
                Edit tenant
              </button>
            )}
            {canResume && (
              <button onClick={(e) => { e.stopPropagation(); setStatus('active'); }}
                      className="btn-ghost text-sm text-emerald-600">
                Resume tenant
              </button>
            )}
            {canSuspend && (
              <button onClick={(e) => { e.stopPropagation(); setStatus('suspended'); }}
                      className="btn-ghost text-sm text-amber-600">
                Suspend tenant
              </button>
            )}
            {canArchive && (
              <button onClick={(e) => {
                e.stopPropagation();
                if (!confirm(`Archive tenant "${t.tenant_id}"? Its license will be cancelled. Spokes will lose access.`)) return;
                api.tenantsArchive(t.tenant_id).then(onChange).catch((e: any) => alert(e.message));
              }} className="btn-ghost text-sm text-tnh-err">
                Archive tenant
              </button>
            )}
          </div>
        </div>
      )}
      {showEdit && (
        <EditTenantModal
          tenant={t}
          me={me}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); onChange(); }}
        />
      )}
    </div>
  );
}

function CreateTenantModal({ existing, onClose, onCreated }: { existing: TenantRow[]; onClose: () => void; onCreated: () => void }) {
  const [cloneMode, setCloneMode] = useState<'scratch' | 'clone'>('scratch');
  const [cloneFrom, setCloneFrom] = useState<string>(existing[0]?.tenant_id ?? '');
  const [tenantId, setTenantId] = useState('');
  const [name, setName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [tier, setTier] = useState<'starter' | 'standard' | 'pro' | 'enterprise'>('standard');
  const [profile, setProfile] = useState<'single_node' | 'compact_ha' | 'production_ha'>('single_node');
  const [planAmount, setPlanAmount] = useState<number>(950000); // $9,500 — current TriNetHub list price
  const [termYears, setTermYears] = useState<1 | 3 | 5>(1);
  const [modules, setModules] = useState<string[]>(['cucm', 'reporting']);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<any | null>(null);

  // When user picks a clone source, pre-fill defaults from it.
  useEffect(() => {
    if (cloneMode !== 'clone') return;
    const src = existing.find((t) => t.tenant_id === cloneFrom);
    if (!src) return;
    try { setModules(JSON.parse(src.modules_enabled || '[]')); } catch {}
    setTier((src.subscription_tier as any) || 'standard');
    setProfile((src.spoke_profile as any) || 'single_node');
  }, [cloneFrom, cloneMode, existing]);

  const ALL_MODULES = ['cucm', 'uccx', 'wxc', 'wxcc', 'cube', 'lgw', 'reporting', 'support', 'qa', 'features'];

  function toggleModule(m: string) {
    setModules((cur) => cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const start = Date.now();
      const end = start + termYears * 365 * 86400 * 1000;
      const r = await api.tenantsCreate({
        tenant_id: tenantId.toLowerCase(), name, billing_email: billingEmail,
        start_date: start, end_date: end,
        subscription_tier: tier, spoke_profile: profile,
        modules_enabled: modules, plan_amount: planAmount, currency: 'USD',
        clone_from: cloneMode === 'clone' ? cloneFrom : undefined,
        notes,
      });
      setCreated(r);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  if (created) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
        <div className="card max-w-lg w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
          <h2 className="font-semibold text-lg">Tenant created ✓</h2>
          <div className="text-sm space-y-1">
            <div>tenant_id: <span className="font-mono">{created.tenant_id}</span></div>
            <div>license_id: <span className="font-mono">{created.license_id}</span></div>
            <div>expires: {new Date(created.end_date).toLocaleDateString()}</div>
            {created.cloned_categories?.length > 0 && (
              <div className="mt-2">
                <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Cloned Config Manager structure</div>
                <div className="text-xs">
                  Categories pre-listed (NO credentials copied): {created.cloned_categories.join(', ')}.
                  Admin must fill in IPs/passwords in the Config Manager.
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-2">
            <button onClick={() => { onCreated(); }} className="btn-primary text-sm">Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-semibold text-lg">Create tenant</h2>

        <div className="flex items-center gap-2 border-b border-tnh-border dark:border-tnh-border-dark text-sm">
          <button type="button" onClick={() => setCloneMode('scratch')}
                  className={`px-4 py-2 -mb-px border-b-2 ${cloneMode === 'scratch' ? 'border-tnh-accent text-tnh-accent font-medium' : 'border-transparent text-gray-500'}`}>
            Start from scratch
          </button>
          <button type="button" onClick={() => setCloneMode('clone')}
                  className={`px-4 py-2 -mb-px border-b-2 ${cloneMode === 'clone' ? 'border-tnh-accent text-tnh-accent font-medium' : 'border-transparent text-gray-500'}`}>
            Clone from existing
          </button>
        </div>

        {cloneMode === 'clone' && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">Source tenant</label>
            <select value={cloneFrom} onChange={(e) => setCloneFrom(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent">
              {existing.map((t) => <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_id} — {t.name}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1">Modules + Configuration Manager category list will be copied. <strong>Credentials and IPs are NOT copied.</strong> The new tenant's admin fills them in.</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">tenant_id (lowercase, [a-z0-9_-])</label>
            <input value={tenantId} onChange={(e) => setTenantId(e.target.value)} required pattern="[a-z0-9_-]{3,40}"
                   className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent font-mono" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Display name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required
                   className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Billing email (Stripe + Xero will use this)</label>
            <input type="email" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} required
                   className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Tier</label>
            <select value={tier} onChange={(e) => setTier(e.target.value as any)}
                    className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent">
              <option value="starter">Starter</option><option value="standard">Standard</option>
              <option value="pro">Pro</option><option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Spoke profile</label>
            <select value={profile} onChange={(e) => setProfile(e.target.value as any)}
                    className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent">
              <option value="single_node">Single node</option>
              <option value="compact_ha">Compact HA</option>
              <option value="production_ha">Production HA</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Annual price (USD)</label>
            <input type="number" min={0} step={100} value={planAmount / 100} onChange={(e) => setPlanAmount(Math.round(Number(e.target.value) * 100))}
                   className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Initial term</label>
            <select value={termYears} onChange={(e) => setTermYears(Number(e.target.value) as 1 | 3 | 5)}
                    className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent">
              <option value={1}>1 year</option><option value={3}>3 years</option><option value={5}>5 years</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Modules enabled</label>
          <div className="flex flex-wrap gap-1.5">
            {ALL_MODULES.map((m) => (
              <button key={m} type="button" onClick={() => toggleModule(m)}
                      className={`pill cursor-pointer ${modules.includes(m) ? 'bg-tnh-accent text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}>
                {m}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={200}
                 className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent" />
        </div>

        {err && <div className="text-sm text-tnh-err">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary">{busy ? 'Creating…' : 'Create tenant'}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit tenant modal — super-admin can change everything, tenant-admin can
// only change billing_email + display name. The PATCH endpoint enforces this
// server-side; the UI just hides fields it knows will be rejected.
// ---------------------------------------------------------------------------
const ALL_MODULES = [
  // Cisco / Webex telephony
  'cucm', 'uccx', 'wxc', 'wxcc', 'cube', 'lgw',
  // Platform
  'reporting', 'support', 'features', 'qa', 'audit',
];

function EditTenantModal({ tenant, me, onClose, onSaved }: {
  tenant: TenantRow; me: Me; onClose: () => void; onSaved: () => void;
}) {
  const isSuper = me.role === 'super_admin';
  const initialModules: string[] = (() => {
    try { return JSON.parse(tenant.modules_enabled || '[]'); } catch { return []; }
  })();
  const toIso = (ms: number | null | undefined) =>
    ms ? new Date(ms).toISOString().slice(0, 10) : '';
  const fromIso = (s: string) => s ? new Date(s).getTime() : null;

  const [name, setName]                   = useState(tenant.name);
  const [billingEmail, setBillingEmail]   = useState(tenant.billing_email || '');
  const [tier, setTier]                   = useState(tenant.subscription_tier || 'standard');
  const [profile, setProfile]             = useState(tenant.spoke_profile || 'single_node');
  const [startDate, setStartDate]         = useState(toIso(tenant.start_date));
  const [endDate,   setEndDate]           = useState(toIso(tenant.end_date));
  const [status,    setStatus]            = useState(tenant.status);
  const [modules,   setModules]           = useState<string[]>(initialModules);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  function toggleModule(m: string) {
    setModules((cur) => cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    // Build a diff — only send fields that actually changed, so the server can
    // pin down the audit event payload and tenant_admin doesn't get 403 for
    // an unchanged protected field.
    const body: any = {};
    if (name           !== tenant.name)            body.name           = name;
    if (billingEmail   !== (tenant.billing_email || '')) body.billing_email = billingEmail;
    if (isSuper) {
      if (tier         !== tenant.subscription_tier) body.subscription_tier = tier;
      if (profile      !== tenant.spoke_profile)     body.spoke_profile     = profile;
      if (status       !== tenant.status)            body.status            = status;
      const sd = fromIso(startDate);
      const ed = fromIso(endDate);
      if (sd !== tenant.start_date) body.start_date = sd;
      if (ed !== tenant.end_date)   body.end_date   = ed;
      const sortedNew = [...modules].sort().join(',');
      const sortedOld = [...initialModules].sort().join(',');
      if (sortedNew !== sortedOld) body.modules_enabled = modules;
    }
    if (Object.keys(body).length === 0) {
      setErr('No changes to save.'); setBusy(false); return;
    }
    try {
      await api.tenantsUpdate(tenant.tenant_id, body);
      onSaved();
    } catch (e: any) {
      setErr(e.message || 'Update failed.');
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-semibold text-lg">Edit tenant <span className="font-mono">{tenant.tenant_id}</span></h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {isSuper ? 'Super-admin · full edit access' : 'Tenant-admin · billing & display only'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Close</button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Display name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required
                   className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Billing email</label>
            <input type="email" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} required
                   className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent" />
          </div>
        </div>

        {isSuper && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Tier</label>
                <select value={tier} onChange={(e) => setTier(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent">
                  <option value="starter">Starter</option>
                  <option value="standard">Standard</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Spoke profile</label>
                <select value={profile} onChange={(e) => setProfile(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent">
                  <option value="single_node">Single node</option>
                  <option value="compact_ha">Compact HA</option>
                  <option value="production_ha">Production HA</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent">
                  <option value="onboarding">Onboarding</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended (disabled)</option>
                  <option value="churning">Churning</option>
                  <option value="deleted">Archived</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start date</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                       className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">License expiration</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                       className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent" />
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-2">Modules enabled — click to toggle</label>
              <div className="flex flex-wrap gap-1.5">
                {ALL_MODULES.map((m) => {
                  const on = modules.includes(m);
                  return (
                    <button key={m} type="button" onClick={() => toggleModule(m)}
                            className={`pill cursor-pointer transition ${on ? 'bg-tnh-accent text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}>
                      {on ? '✓ ' : ''}{m}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                Disabling a module hides its sidebar nav for tenant-admins and removes API access on the next session. Spoke-side adapters stop polling within 60 s.
              </p>
            </div>
          </>
        )}

        {!isSuper && (
          <div className="text-xs text-gray-500 italic">
            Tenant-admins can only edit display name and billing email. Tier, dates, modules, and status require super-admin.
          </div>
        )}

        {err && <div className="text-sm text-tnh-err">{err}</div>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary">
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
